import React, { ReactElement, Fragment, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link, NavLink, useLocation } from 'react-router-dom';
import ReactIcon from '@mdi/react';
import { mdiArrowRight } from '@mdi/js';

import { useMenu } from 'features/menu';
import { Box, ButtonIcon, Flex, Logo, Hr } from 'components';
import { createUrl } from 'app/utils';
import { useTracking } from 'features/tracking';
import { useResources } from 'features/resources';

const OverlayButton = styled(Flex)`
  position: relative;
  background: rgba(46, 61, 60, 0.9);

  &::after {
    display: block;
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 12;
    top: 0;
    left: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 50%);
    opacity: 0.6;
  }

  ${({ theme: { mediaQueries } }) => {
    return `
      display: none;

      ${mediaQueries.tablet} {
        display: block;
      }
    `;
  }}
`;

const Icon = styled(ReactIcon)`
  width: 24px;
  color: ${({ theme }) => theme.colors.primary_400};
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
`;

const linkStyle = css`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary_400};
  position: relative;
  outline: none;

  ${({ theme }) => `${theme.fontTypes.type1}`}

  &:hover {
    background: ${({ theme }) => theme.colors.dark_350};
  }

  &.active {
    background: ${({ theme }) => theme.colors.primary_400};
    color: ${({ theme }) => theme.colors.white};

    ${Icon} {
      color: ${({ theme }) => theme.colors.white};
    }
  }

  ${({ theme: { mediaQueries, fontSizes } }) => {
    return `
      font-size: ${fontSizes[1]};
      min-height: 60px;
      padding-left: 20px;

      ${mediaQueries.tablet} {
        font-size: ${fontSizes[1]};
        padding-left: 30px;
      }

      ${mediaQueries.desktop} {
        font-size: ${fontSizes[2]};
        min-height: 80px;
      }
    `;
  }}
`;

const CustomNavLink = styled(NavLink)`
  ${linkStyle}
`;

const CustomLink = styled.a`
  ${linkStyle}
`;

const MenuLogo = styled(Logo)`
  max-height: 26px !important;
`;

const Menu = (): ReactElement => {
  const { isOpened, currentPathname, setCurrentPathname, setMenuOpened } = useMenu();
  const location = useLocation();
  const { eventTracker } = useTracking();
  const {
    usingTeachers,
    usingReports,
    usingExams,
    showCoursesInMenu,
    schools: { schoolBenndorf, schoolMPC },
  } = useResources();

  let menuProductsLabel = 'Cursos';
  if (schoolBenndorf) {
    menuProductsLabel = 'Produtos';
  } else if (schoolMPC) {
    menuProductsLabel = 'Meu painel';
  }

  const menu = [
    {
      name: 'Home',
      url: createUrl('dashboard'),
      exact: true,
      external: false,
    },
    {
      name: menuProductsLabel,
      url: createUrl('productsList'),
      show: showCoursesInMenu,
    },
    {
      name: 'Professores',
      url: createUrl('teachersList'),
      show: usingTeachers,
    },
    {
      name: 'Relatórios',
      url: createUrl('reportsList'),
      show: usingReports,
    },
    {
      name: 'Avaliações',
      url: createUrl('exams/list'),
      show: usingExams,
    },
    {
      name: 'Sair',
      url: createUrl('logout'),
      onClick: () => {
        eventTracker({
          category: 'Usuário',
          action: 'Logout',
          label: 'Menu hamburguer',
        });
      },
    },
  ];

  useEffect(() => {
    if (location.pathname !== currentPathname) {
      window.scrollTo(0, 0);
      setCurrentPathname(location.pathname);
      if (isOpened) setMenuOpened(false);
    }
  }, [location.pathname, currentPathname]);

  return isOpened ? (
    <Flex width="100vw" height="100vh" position="fixed" flexWrap="nowrap" zIndex={10000}>
      <Flex flexShrink={0} width={{ m: '100%', t: 450 }} bg="dark_400">
        <Flex justifyContent="space-between" flexDirection="column">
          <Flex flexGrow={0} flexDirection="column">
            <Flex flexGrow={0} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
              <ButtonIcon
                width={{ m: 60, d: 80 }}
                height={{ m: 60, d: 80 }}
                justifyContent="center"
                icon="close"
                onClick={() => setMenuOpened(false)}
                size="large"
                flexShrink={0}
                bg="transparent"
                color="white"
                colorHover="primary_400"
                colorClick="primary_200"
                aria-label="Fechar menu"
              />

              <Box textAlign="right" pr={{ m: 30 }}>
                <Link to="/" onClick={() => setMenuOpened(false)}>
                  <MenuLogo />
                </Link>
              </Box>
            </Flex>

            <Flex>
              <Box>
                <Hr color="dark_350" m={0} />

                {menu
                  .filter((item) => typeof item.show === 'undefined' || item.show === true)
                  .map((item, index) => (
                    <Fragment key={`menu-${index.toString()}`}>
                      {!item.external ? (
                        <CustomNavLink
                          to={item.url}
                          activeClassName="active"
                          end={item.exact || false}
                          onClick={() => {
                            if (item.onClick) item.onClick();
                          }}
                        >
                          {item.name}

                          <Icon path={mdiArrowRight} />
                        </CustomNavLink>
                      ) : (
                        <CustomLink href={item.url} target="_blank" rel="noreferrer">
                          {item.name}

                          <Icon path={mdiArrowRight} />
                        </CustomLink>
                      )}

                      <Hr color="dark_350" m={0} />
                    </Fragment>
                  ))}
              </Box>
            </Flex>
          </Flex>

          {/* <Flex flexGrow={0} p={30} justifyContent="center">
            <Link to="/admin">
              <ButtonIcon
                icon="gear"
                label="Administração"
                size="large"
                bg="transparent"
                colorHover="primary_400"
              />
            </Link>
          </Flex> */}
        </Flex>
      </Flex>

      <OverlayButton width="100%" onClick={() => setMenuOpened(false)} flexGrow={{ m: 0, t: 1 }} />
    </Flex>
  ) : (
    <></>
  );
};

export default React.memo(Menu);
